<script setup lang="ts">
import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import Image from '@tiptap/extension-image'
import { TextSelection } from '@tiptap/pm/state'
import { Bold, Italic, Underline, Heading, Heading1, Heading2, Heading3, Heading4, Heading5, Heading6, ListOrdered, List, Quote, Link as LinkIcon } from 'lucide-vue-next'
import StarterKit from '@tiptap/starter-kit'
import { TableKit } from '@tiptap/extension-table'
import UiToggleButton from './UiToggleButton.vue'
import UiFieldContainer from './UiFieldContainer.vue'
import { ToggleValue } from './shared/toggleGroupContext'
import UiVerticalDivider from './UiVerticalDivider.vue'
import UiText from './UiText.vue'
import UiIcon from './UiIcon.vue'
import UiDialog from './UiDialog.vue'
import UiInputField from './UiInputField.vue'
import UiSwitch from './UiSwitch.vue'

export interface UiRichTextImageEmbedResult {
    url: string
    mediaId?: string
    alt?: string
}

export interface UiRichTextEditorProps {
    readonly?: boolean
    disabled?: boolean
    placeholder?: string
    name?: string
    size?: 'md' | 'lg'
    hint?: string
    error?: string
    invalid?: boolean
    id?: string
    label?: string
    rows?: number
    resize?: 'vertical'
    fill?: boolean
    onImageEmbed?: (file: File) => Promise<UiRichTextImageEmbedResult>
    imageUploadLabel?: string
    linkFollowControls?: boolean
}

const props = withDefaults(defineProps<UiRichTextEditorProps>(), {
    readonly: false,
    disabled: false,
    linkFollowControls: false,
})

const model = defineModel<string>({ default: '' })

const emit = defineEmits<{
    (e: 'validate'): void
}>()

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

const isHeadingMenuOpen = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const isUploadingImage = ref(false)
const editorContainer = ref<HTMLElement | null>(null)
const resizedHeight = ref<number | null>(null)
const resizeStart = ref<{ pointerY: number; height: number } | null>(null)

function toggleHeadingMenu() {
    isHeadingMenuOpen.value = !isHeadingMenuOpen.value
}

const isHeading1Active = computed(() => editor.value?.isActive('heading', { level: 1 }))
const isHeading2Active = computed(() => editor.value?.isActive('heading', { level: 2 }))
const isHeading3Active = computed(() => editor.value?.isActive('heading', { level: 3 }))
const isHeading4Active = computed(() => editor.value?.isActive('heading', { level: 4 }))
const isHeading5Active = computed(() => editor.value?.isActive('heading', { level: 5 }))
const isHeading6Active = computed(() => editor.value?.isActive('heading', { level: 6 }))
const isOrderedListActive = computed(() => editor.value?.isActive('orderedList'))
const isBulletListActive = computed(() => editor.value?.isActive('bulletList'))
const isBlockquoteActive = computed(() => editor.value?.isActive('blockquote'))
const isItalicActive = computed(() => editor.value?.isActive('italic'))
const isUnderlineActive = computed(() => editor.value?.isActive('underline'))

const RichTextImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            dataMediaId: {
                default: null,
                parseHTML: element => element.getAttribute('data-media-id'),
                renderHTML: attributes => {
                    if (!attributes.dataMediaId) return {}
                    return { 'data-media-id': attributes.dataMediaId }
                },
            },
        }
    },
})

function imageAttrs(result: UiRichTextImageEmbedResult) {
    return {
        src: result.url,
        alt: result.alt || '',
        dataMediaId: result.mediaId || null,
    }
}

function imageFilesFromList(files: FileList | File[] | null | undefined): File[] {
    if (!files) return []
    return Array.from(files).filter(file => file.type.startsWith('image/'))
}

async function embedImageFiles(files: File[]) {
    if (!props.onImageEmbed || files.length === 0) return

    isUploadingImage.value = true
    try {
        const results: UiRichTextImageEmbedResult[] = []
        for (const file of files) {
            const result = await props.onImageEmbed(file)
            if (!result.url) {
                throw new Error('Image embed upload did not return a URL.')
            }
            results.push(result)
        }

        for (const result of results) {
            editor.value?.chain().focus().insertContent({
                type: 'image',
                attrs: imageAttrs(result),
            }).run()
        }
    } catch (error) {
        console.error('[UiRichTextEditor] Image embed failed:', error)
    } finally {
        isUploadingImage.value = false
    }
}

function openImagePicker() {
    if (props.readonly || props.disabled || !props.onImageEmbed || isUploadingImage.value) return
    fileInput.value?.click()
}

function handleImageInputChange(event: Event) {
    const input = event.target as HTMLInputElement
    const files = imageFilesFromList(input.files)
    input.value = ''
    void embedImageFiles(files)
}

const selectedHeadingLevel = (value: ToggleValue | null) => {
    if (value) {
        const parsedValue = parseInt(value as string);
        editor.value?.chain().focus().toggleHeading({ level: parsedValue as HeadingLevel }).run()
    }
}

const isLinkDialogOpen = ref(false)
const link = reactive({
    range: { from: 0, to: 0 },
    originalText: '',
    existingRel: null as string | null,
    editing: false,
    text: '',
    url: '',
    nofollow: false,
    urlError: '',
})

watch(() => link.url, () => {
    link.urlError = ''
})

const isLinkActive = computed(() => editor.value?.isActive('link'))

function openLinkDialog() {
    const ed = editor.value
    if (!ed || props.readonly || props.disabled) return

    let attrs: Record<string, unknown> = {}
    link.editing = ed.isActive('link')
    if (link.editing) {
        ed.chain().extendMarkRange('link').run()
        attrs = ed.getAttributes('link')
    }

    const { from, to } = ed.state.selection
    link.range = { from, to }
    link.originalText = ed.state.doc.textBetween(from, to, ' ')
    link.existingRel = typeof attrs.rel === 'string' ? attrs.rel : null
    link.text = link.originalText
    link.url = typeof attrs.href === 'string' ? attrs.href : ''
    link.nofollow = /\bnofollow\b/.test(link.existingRel || '')
    link.urlError = ''
    isLinkDialogOpen.value = true
}

const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:']

/** Returns the normalized URL, '' when empty, or null when the scheme is not allowed. */
function normalizeLinkUrl(raw: string): string | null {
    const url = raw.trim()
    if (!url) return ''
    if (url.startsWith('//')) return `https:${url}`
    if (url.startsWith('/') || url.startsWith('#')) return url

    const normalized = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url) ? url : `https://${url}`
    try {
        return ALLOWED_LINK_PROTOCOLS.includes(new URL(normalized).protocol) ? normalized : null
    } catch {
        return null
    }
}

function applyLink() {
    const ed = editor.value
    if (!ed) return

    const url = normalizeLinkUrl(link.url)
    if (!url) {
        link.urlError = url === ''
            ? 'Enter a link URL.'
            : 'Links must use http(s), mailto: or tel:.'
        return
    }

    const text = link.text.trim() || url
    // Without follow controls, preserve whatever rel the link already had.
    const rel = props.linkFollowControls
        ? (link.nofollow ? 'nofollow' : null)
        : link.existingRel
    const { range, originalText } = link

    if (text === originalText && range.from !== range.to) {
        ed.chain()
            .focus()
            .setTextSelection(range)
            .setLink({ href: url, rel })
            .setTextSelection(range.to)
            .unsetMark('link')
            .run()
    } else {
        // Replacing the text loses per-character formatting, so carry over the
        // marks from the start of the original range (a bold link stays bold).
        const carriedMarks = range.from !== range.to
            ? (ed.state.doc.nodeAt(range.from)?.marks || [])
                .filter(mark => mark.type.name !== 'link')
                .map(mark => ({ type: mark.type.name, attrs: mark.attrs }))
            : []
        ed.chain()
            .focus()
            .insertContentAt(range, {
                type: 'text',
                text,
                marks: [...carriedMarks, { type: 'link', attrs: { href: url, rel } }],
            })
            .unsetMark('link')
            .run()
    }

    isLinkDialogOpen.value = false
}

function removeLink() {
    const ed = editor.value
    if (!ed) return

    ed.chain().focus().setTextSelection(link.range).unsetLink().run()
    isLinkDialogOpen.value = false
}

const editor = useEditor({
    content: model.value || '',
    extensions: [
        StarterKit.configure({
            heading: { levels: [1, 2, 3, 4, 5, 6] },
            link: {
                openOnClick: false,
                defaultProtocol: 'https',
                HTMLAttributes: { rel: null, target: null },
            },
        }),
        TableKit,
        RichTextImage,
    ],
    editorProps: {
        attributes: {
            class: 'focus:outline-none'
        },
        handlePaste: (_view, event) => {
            if (!props.onImageEmbed) return false
            const files = imageFilesFromList(event.clipboardData?.files)
            if (files.length === 0) return false

            event.preventDefault()
            void embedImageFiles(files)
            return true
        },
        handleDrop: (view, event) => {
            if (!props.onImageEmbed) return false
            const files = imageFilesFromList(event.dataTransfer?.files)
            if (files.length === 0) return false

            event.preventDefault()
            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY })
            if (coordinates) {
                const selection = TextSelection.near(view.state.doc.resolve(coordinates.pos))
                view.dispatch(view.state.tr.setSelection(selection))
            }
            void embedImageFiles(files)
            return true
        },
    },
    editable: !props.readonly && !props.disabled,
    onBlur: () => {
        emit('validate')
    }
})

function handleEditorUpdate() {
    const html = editor.value?.getHTML() || ''
    if (html !== model.value) {
        model.value = html
    }
}

watch(editor, (ed, prev) => {
    // detach previous
    if (prev) prev.off('update', handleEditorUpdate)
    if (ed) ed.on('update', handleEditorUpdate)
}, { immediate: true })

onBeforeUnmount(() => {
    const ed = editor.value
    if (ed) ed.off('update', handleEditorUpdate)
    stopResize()
})

watch(() => [props.readonly, props.disabled] as const, ([readonly, disabled]) => {
    // Changing editability must not emit an update with the previous content.
    editor.value?.setEditable(!(readonly || disabled), false)
})

watch(model, (newValue) => {
    const current = editor.value?.getHTML()
    if (newValue != null && newValue !== current) {
        editor.value?.commands.setContent(newValue, { emitUpdate: false })
    }
})

const isBold = computed(() => editor.value?.isActive('bold'))

const editorHeight = computed(() => {
    if (props.rows) {
        // Calculate height based on rows. Using line-height of 1.75rem (28px) from prose class
        // and accounting for some padding
        const lineHeight = 1.75 // rem
        const heightInRem = lineHeight * props.rows
        return `${heightInRem}rem`
    }
    return undefined
})

const editorContentInsetClass = computed(() => {
    if (props.size === 'lg') return 'px-6'
    return 'px-4'
})
const placeholderInsetClass = computed(() => {
    if (props.resize === 'vertical') return `${editorContentInsetClass.value} pt-6`
    return editorContentInsetClass.value
})

const fieldInset = computed(() => props.size === 'lg' ? '1.5rem' : '1rem')
const fieldToolbarGap = '1rem'
const fieldBottomInset = '0.5rem'
const resizeEditorHeight = computed(() => {
    if (!editorHeight.value) return undefined
    return `calc(${editorHeight.value} + ${fieldToolbarGap} + ${fieldBottomInset})`
})

const editorContainerStyle = computed(() => {
    const edgeStyle = {
        marginLeft: `-${fieldInset.value}`,
        marginRight: `-${fieldInset.value}`,
        width: `calc(100% + ${fieldInset.value} * 2)`,
    }

    if (props.fill) return edgeStyle
    if (resizedHeight.value !== null) {
        return {
            ...edgeStyle,
            ...(props.resize === 'vertical' ? {
                marginTop: `-${fieldToolbarGap}`,
                marginBottom: `-${fieldBottomInset}`,
            } : {}),
            height: `${resizedHeight.value}px`,
            minHeight: props.resize === 'vertical' ? resizeEditorHeight.value : editorHeight.value,
        }
    }
    if (!editorHeight.value) return undefined

    if (props.resize === 'vertical') {
        return {
            ...edgeStyle,
            marginTop: `-${fieldToolbarGap}`,
            marginBottom: `-${fieldBottomInset}`,
            height: resizeEditorHeight.value,
            minHeight: resizeEditorHeight.value,
        }
    }

    return {
        ...edgeStyle,
        height: editorHeight.value,
        maxHeight: editorHeight.value,
    }
})

function handleResizePointerMove(event: PointerEvent) {
    if (!resizeStart.value || !editorContainer.value) return

    const minHeight = Number.parseFloat(getComputedStyle(editorContainer.value).minHeight) || 0
    resizedHeight.value = Math.max(minHeight, resizeStart.value.height + event.clientY - resizeStart.value.pointerY)
}

function stopResize() {
    resizeStart.value = null
    window.removeEventListener('pointermove', handleResizePointerMove)
    window.removeEventListener('pointerup', stopResize)
    window.removeEventListener('pointercancel', stopResize)
}

function startResize(event: PointerEvent) {
    if (props.resize !== 'vertical' || !editorContainer.value) return

    event.preventDefault()
    resizeStart.value = {
        pointerY: event.clientY,
        height: editorContainer.value.getBoundingClientRect().height,
    }
    window.addEventListener('pointermove', handleResizePointerMove)
    window.addEventListener('pointerup', stopResize)
    window.addEventListener('pointercancel', stopResize)
}
</script>

<template>
    <UiFieldContainer :size="props.size" flexable :invalid="props.invalid" :hint="props.hint" :error="props.error"
        :describedByIdBase="props.id || props.name" :align="props.fill ? 'stretch' : 'center'"
        :class="props.fill ? 'min-h-0 flex-1' : undefined"
        :root-class="props.fill ? 'min-h-0 flex-1' : undefined" toolbar>
        <template #toolbar>
            <div class="w-full relative flex flex-row gap-1">
                <UiToggleButton variant="ghost" @click="editor?.chain().focus().toggleBold().run()" shape="icon"
                    :modelValue="isBold">
                    <template #before_label>
                        <Bold :size="16" />
                    </template>
                </UiToggleButton>
                <UiToggleButton variant="ghost" @click="editor?.chain().focus().toggleItalic().run()" shape="icon" :modelValue="isItalicActive">
                    <template #before_label>
                        <Italic :size="16" />
                    </template>
                </UiToggleButton>
                <UiToggleButton variant="ghost" @click="editor?.chain().focus().toggleUnderline().run()" shape="icon" :modelValue="isUnderlineActive">
                    <template #before_label>
                        <Underline :size="16" />
                    </template>
                </UiToggleButton>
                <UiToggleButton variant="ghost" @click="toggleHeadingMenu" shape="icon">
                    <template #before_label>
                        <Heading :size="16" />
                    </template>
                </UiToggleButton>
                <div v-show="isHeadingMenuOpen"
                    class="flex flex-row gap-1 absolute bottom-0 translate-y-full p-2 bg-se-white border-se-gray-300 border-1 rounded-lg z-30">
                    <UiToggleButton variant="ghost" value="1" shape="icon" :modelValue="isHeading1Active"
                        @click="selectedHeadingLevel('1')">
                        <template #before_label>
                            <Heading1 :size="16" />
                        </template>
                    </UiToggleButton>
                    <UiToggleButton variant="ghost" value="2" shape="icon" :modelValue="isHeading2Active"
                        @click="selectedHeadingLevel('2')">
                        <template #before_label>
                            <Heading2 :size="16" />
                        </template>
                    </UiToggleButton>
                    <UiToggleButton variant="ghost" value="3" shape="icon" :modelValue="isHeading3Active"
                        @click="selectedHeadingLevel('3')">
                        <template #before_label>
                            <Heading3 :size="16" />
                        </template>
                    </UiToggleButton>
                    <UiToggleButton variant="ghost" value="4" shape="icon" :modelValue="isHeading4Active"
                        @click="selectedHeadingLevel('4')">
                        <template #before_label>
                            <Heading4 :size="16" />
                        </template>
                    </UiToggleButton>
                    <UiToggleButton variant="ghost" value="5" shape="icon" :modelValue="isHeading5Active"
                        @click="selectedHeadingLevel('5')">
                        <template #before_label>
                            <Heading5 :size="16" />
                        </template>
                    </UiToggleButton>
                    <UiToggleButton variant="ghost" value="6" shape="icon" :modelValue="isHeading6Active"
                        @click="selectedHeadingLevel('6')">
                        <template #before_label>
                            <Heading6 :size="16" />
                        </template>
                    </UiToggleButton>
                </div>
                <UiVerticalDivider height="auto" class="mx-1" />
                <UiToggleButton variant="ghost" shape="icon" @click="editor?.chain().focus().toggleOrderedList().run()" :modelValue="isOrderedListActive">
                    <template #before_label>
                        <ListOrdered :size="16" />
                    </template>
                </UiToggleButton>
                <UiToggleButton variant="ghost" shape="icon" @click="editor?.chain().focus().toggleBulletList().run()" :modelValue="isBulletListActive">
                    <template #before_label>
                        <List :size="16" />
                    </template>
                </UiToggleButton>
                <UiToggleButton
                    variant="ghost"
                    shape="icon"
                    aria-label="Toggle callout"
                    @click="editor?.chain().focus().toggleBlockquote().run()"
                    :modelValue="isBlockquoteActive"
                >
                    <template #before_label>
                        <Quote :size="16" />
                    </template>
                </UiToggleButton>
                <UiVerticalDivider height="auto" class="mx-1" />
                <UiToggleButton
                    variant="ghost"
                    shape="icon"
                    aria-label="Insert link"
                    data-testid="rich-text-link-button"
                    :modelValue="isLinkActive"
                    @click="openLinkDialog"
                >
                    <template #before_label>
                        <LinkIcon :size="16" />
                    </template>
                </UiToggleButton>
                <template v-if="props.onImageEmbed">
                    <UiToggleButton
                        variant="ghost"
                        shape="icon"
                        :disabled="props.readonly || props.disabled || isUploadingImage"
                        :aria-label="props.imageUploadLabel || 'Embed image'"
                        @click="openImagePicker"
                    >
                        <template #before_label>
                            <UiIcon name="image" class="h-4 w-4" />
                        </template>
                    </UiToggleButton>
                    <input
                        ref="fileInput"
                        data-testid="rich-text-image-input"
                        type="file"
                        accept="image/*"
                        class="hidden"
                        :aria-label="props.imageUploadLabel || 'Embed image'"
                        @change="handleImageInputChange"
                    >
                </template>
                <div v-if="$slots['toolbar-extra']" class="ml-auto flex items-center">
                    <slot name="toolbar-extra" />
                </div>
            </div>
        </template>
        <div
            ref="editorContainer"
            class="relative w-full overflow-hidden rounded-b-lg ui-rich-text-editor-frame"
            :class="props.fill ? 'h-full max-h-full min-h-0 flex-1 ui-rich-text-editor-fill' : undefined"
            :style="editorContainerStyle"
        >
            <div class="relative h-full w-full overflow-x-hidden overflow-y-auto ui-rich-text-editor-scroll">
                <EditorContent
                    class="w-full prose"
                    :class="[
                        props.fill ? 'min-h-full' : undefined,
                        editorContentInsetClass,
                        props.resize === 'vertical' ? 'pt-6 pb-7' : undefined,
                    ]"
                    :editor="editor"
                    :id="props.id || props.name"
                    :name="props.name"
                />
                <UiText
                    :text="props.placeholder"
                    as="p"
                    class="text-se-gray-700 w-full absolute pointer-events-none top-0"
                    :class="placeholderInsetClass"
                    v-if="editor?.isEmpty && !editor?.isFocused" />
            </div>
        </div>
        <button
            v-if="props.resize === 'vertical'"
            type="button"
            class="absolute left-0 bottom-0 z-10 flex h-6 w-6 items-center justify-center border-0 p-0 text-se-gray-500 opacity-80 cursor-ns-resize transition-colors hover:text-se-gray-700 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-se-gray-400 focus-visible:ring-offset-1"
            aria-label="Resize editor vertically"
            data-testid="rich-text-resize-handle"
            @pointerdown="startResize"
        >
            <UiIcon name="resize-corner" class="h-3.5 w-3.5" />
        </button>
        <UiDialog
            v-if="isLinkDialogOpen"
            v-model="isLinkDialogOpen"
            :title="link.editing ? 'Edit link' : 'Insert link'"
            size="sm"
            :primary-action-label="link.editing ? 'Save link' : 'Insert link'"
            :secondary-action-label="link.editing ? 'Remove link' : undefined"
            @primary-action="applyLink"
            @secondary-action="removeLink"
        >
            <div class="flex flex-col gap-5">
                <div class="flex flex-col gap-1.5">
                    <UiText as="label" variant="label" text="Text" />
                    <UiInputField
                        v-model="link.text"
                        name="rich-text-link-text"
                        placeholder="Link text"
                        @keydown.enter.prevent="applyLink"
                    />
                </div>
                <div class="flex flex-col gap-1.5">
                    <UiText as="label" variant="label" text="URL" />
                    <UiInputField
                        v-model="link.url"
                        name="rich-text-link-url"
                        placeholder="https://example.com"
                        :invalid="Boolean(link.urlError)"
                        :error="link.urlError || undefined"
                        @keydown.enter.prevent="applyLink"
                    />
                </div>
                <div v-if="props.linkFollowControls" class="flex flex-col gap-1.5">
                    <UiSwitch
                        v-model="link.nofollow"
                        label="Nofollow link"
                        name="rich-text-link-nofollow"
                    />
                    <UiText
                        as="p"
                        variant="caption-normal"
                        class="text-se-gray-500"
                        text="Ask search engines not to follow this link or pass ranking credit to it."
                    />
                </div>
            </div>
        </UiDialog>
    </UiFieldContainer>
</template>

<style scoped>
.ui-rich-text-editor-fill :deep(.ProseMirror) {
    min-height: 100%;
}

.ui-rich-text-editor-scroll {
    scrollbar-gutter: stable;
}

.ui-rich-text-editor-scroll::-webkit-scrollbar {
    width: 12px;
}

.ui-rich-text-editor-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.ui-rich-text-editor-scroll::-webkit-scrollbar-thumb {
    background-color: rgb(156 163 175 / 0.7);
    border-radius: 999px;
    border: 3px solid transparent;
    background-clip: content-box;
}

:deep(.ProseMirror img) {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
    display: block;
}

</style>
